import {
  Box,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  SimpleGrid,
  Text,
  Tooltip,
} from '@chakra-ui/react'
import React, { useState } from 'react'
import { useLanguage } from '../../../hooks/utility/useLanguage'
import { availableIcons, getIconByName } from '../../../utils/iconUtils'
import { useClient } from '../../../core/client/ClientProvider'

export { availableIcons, availableIcons as iconOptions } from '../../../utils/iconUtils'

interface IconSelectorProps {
  selectedIcon: string
  onIconSelect: (iconName: string) => void
}

export const IconSelector: React.FC<IconSelectorProps> = ({ selectedIcon, onIconSelect }) => {
  const { t } = useLanguage()
  const [isOpen, setIsOpen] = useState(false)
  const { config } = useClient()
  const choices = config?.theme.icons?.agentChoices ?? availableIcons.map(icon => icon.name)

  return (
    <Popover
      isOpen={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      placement='bottom-start'
      isLazy
    >
      <PopoverTrigger>
        <Box
          as='button'
          type='button'
          aria-label={t('iconSelector.chooseIcon')}
          p={3}
          borderRadius='chat.md'
          borderWidth='1px'
          borderColor='app.border'
          bg='chat.iconSurface'
          color='chat.iconInk'
          display='flex'
          alignItems='center'
          justifyContent='center'
          boxSize={12}
          _hover={{ borderColor: 'chat.focus' }}
          _focusVisible={{ outline: '2px solid', outlineColor: 'chat.focus', outlineOffset: '2px' }}
        >
          {getIconByName(selectedIcon || 'Bot', 22) || getIconByName('Bot', 22)}
        </Box>
      </PopoverTrigger>
      {/* Keep the picker inside the modal's accessible/focusable subtree. */}
      <PopoverContent
        w='360px'
        maxW='calc(100vw - 32px)'
        bg='app.bg'
        borderColor='app.border'
        borderRadius='lg'
        boxShadow='lg'
        zIndex={1501}
      >
        <PopoverBody p={4}>
          <Text fontSize='sm' color='app.text.muted' mb={3}>
            {t('iconSelector.chooseIcon')}
          </Text>
          <SimpleGrid columns={6} spacing={2}>
            {choices.map(name => (
              <Tooltip key={name} label={name} placement='top'>
                <Box
                  as='button'
                  type='button'
                  aria-label={name}
                  aria-pressed={selectedIcon === name}
                  onClick={() => {
                    onIconSelect(name)
                    setIsOpen(false)
                  }}
                  borderRadius='4px'
                  borderWidth='1px'
                  borderColor={selectedIcon === name ? 'chat.focus' : 'transparent'}
                  bg={selectedIcon === name ? 'chat.iconSurface' : 'app.bg'}
                  color={selectedIcon === name ? 'chat.iconInk' : 'app.text.primary'}
                  display='flex'
                  alignItems='center'
                  justifyContent='center'
                  w='100%'
                  h={9}
                  _hover={{ bg: 'buttonHover' }}
                  _focusVisible={{ outline: '2px solid', outlineColor: 'chat.focus', outlineOffset: '2px' }}
                >
                  {getIconByName(name, 20)}
                </Box>
              </Tooltip>
            ))}
          </SimpleGrid>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}
